import { apiClient } from '@/core/api/client';
import { downloadBlob, fileNameFromDisposition } from '@/shared/utils/downloadBlob';
import type { V2AssessmentReport } from './v2ReportApi';

/** Feature 095 — full results report (authed: client always; advisor if shared). */

export type ReportPillar = {
  key: string;
  name: string;
  num: number;
  raw: number;
  pct: number;
  isGap: boolean;
};

/** A link (seoUrl → Resource Hub; href → in-app feature) or plain-text label. */
export type ReportResource = { label: string; seoUrl?: string; href?: string };

export type ReportAction = {
  num: number;
  title: string;
  body: string;
  learn: ReportResource[];
  plan: ReportResource[];
  connect: ReportResource[];
  hasLearn: boolean;
  hasPlan: boolean;
  hasConnect: boolean;
};

/** Longevity Planning Framework content for one Top-Opportunity (gap) pillar. */
export type ReportRecommendation = {
  pillarKey: string;
  /** Opportunity rank (1 = highest priority). */
  rank: number;
  name: string;
  pctLabel: string;
  /** The pillar Core Question. */
  core: string;
  means: string;
  actions: ReportAction[];
  guidance: string;
  serviceName: string;
  serviceDesc: string;
  /** A `/providers/:id` route — renders a "Meet your Care Concierge" link. */
  serviceHref: string | null;
  advisorNote: string | null;
  hasAdvisorNote: boolean;
};

/** One rung of the readiness band ladder shown under the overall score. */
export type ReadinessBand = {
  category: string;
  label: string;
  range: string;
  /** Continuous bounds on the 0–100 scale, for the horizontal band bar. */
  min: number;
  max: number;
};

export type AssessmentReport = {
  clientName: string;
  advisorName: string;
  /** True when the signed-in viewer is the client — drives second-person copy. */
  viewerIsClient: boolean;
  completedAt: string | null;
  overallScore: number;
  category: string;
  categoryLabel: string;
  categoryDescription: string;
  readinessBands: ReadinessBand[];
  vision: string | null;
  concern: string | null;
  pillars: ReportPillar[];
  recommendations: ReportRecommendation[];
};

/**
 * One route, two shapes: the backend picks the report version from the
 * assessment's stored question set, so a link sent before MVP 2 keeps resolving
 * to the V1 report. Narrow the result with `isV2Report` before rendering.
 */
export async function getAssessmentReport(
  id: string,
): Promise<AssessmentReport | V2AssessmentReport> {
  const response = await apiClient.get<AssessmentReport | V2AssessmentReport>(
    `/longevity-assessments/${encodeURIComponent(id)}/report`,
  );
  return response.data;
}

/**
 * Fetches an authed PDF and hands it to the browser.
 *
 * Takes the path because there are two documents — the client's report and the
 * advisor's confidential one — and only the endpoint differs.
 *
 * Saving and naming go through the shared helpers, which the care-plan and care
 * concierge downloads already use. This file had its own copy of both, and the
 * copy read only the plain `filename=` form — so a name the server sent as RFC
 * 5987 `filename*=UTF-8''…` arrived percent-encoded or not at all, while the
 * same response downloaded correctly everywhere else in the app.
 */
export async function downloadPdf(path: string, fallbackFilename: string): Promise<string> {
  const res = await apiClient.get<Blob>(path, { responseType: 'blob' });
  const blob =
    res.data instanceof Blob
      ? res.data
      : new Blob([res.data as ArrayBuffer], { type: 'application/pdf' });

  const filename = fileNameFromDisposition(
    (res.headers as Record<string, string | undefined>)['content-disposition'],
    fallbackFilename,
  );
  downloadBlob(blob, filename);
  return filename;
}

/** The client's report, whichever question set the assessment was sent with. */
export const downloadReportPdf = (
  id: string,
  fallbackFilename = 'longevity-readiness-report.pdf',
): Promise<string> =>
  downloadPdf(
    `/longevity-assessments/${encodeURIComponent(id)}/report/pdf`,
    fallbackFilename,
  );
